import React, { useState, useEffect, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import {
  Plus, Trash2, Pencil, Download, Mail, Check, X, Settings,
  History, Copy, Server, ChevronLeft, ChevronRight, Search,
  AlertTriangle, Loader2, Save, ListChecks, Activity, Send, Radar
} from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ── Backend API ──────────────────────────────────────────────────────────
const API_BASE =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL) ||
  (typeof window !== "undefined" ? `${window.location.protocol}//${window.location.hostname}/api` : "/api");

const apiFetch = async (path, opts = {}) => {
  let token = "";
  try { token = localStorage.getItem("token") || ""; } catch (e) {}
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
};

const STATUS = {
  W: { label: "Working", short: "W", color: "#15803D", bg: "#DCFCE7", border: "#86EFAC" },
  NW: { label: "Not working", short: "NW", color: "#B91C1C", bg: "#FEE2E2", border: "#FCA5A5" },
  NA: { label: "N/A", short: "NA", color: "#B45309", bg: "#FEF3C7", border: "#FCD34D" },
};
const STATUS_ORDER = ["W", "NW", "NA", null];

function uid(prefix) {
  return prefix + "_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}
function makeItems(pairs) {
  return pairs.map(([name, ip], i) => ({ id: "item_" + (i + 1), name, values: { "IP / Address": ip } }));
}

// ── Checklist definitions (each is its own space: own fields, items,
// statuses, history — scoped server-side by `key`) ─────────────────────
const CHECKLISTS = [
  {
    key: "main",
    label: "Bengaluru HQ",
    defaultFields: ["IP / Address"],
    defaultItems: makeItems([
      ["AD", "172.16.5.2"],
      ["Gateway", "172.16.10.1"],
      ["Antivirus Update (Trellix)", "172.16.5.21"],
      ["TATA", "14.142.4.2"],
      ["Mindteck regular", "Wifi"],
      ["Mindteck guest", "Wifi"],
      ["Mail Server VM", "172.16.5.205"],
      ["MTKFS", "172.16.5.25"],
      ["Finance printer", "172.16.5.90"],
      ["Git lab", "172.16.5.140"],
      ["Antivirus Dashboard outdated check", "Trellix"],
      ["Camera", "Ganga / Eds / Board room"],
      ["Mail", "Application check list"],
      ["Git lab backup", "Daily Backup"],
      ["HR Printer", "172.16.5.90"],
      ["Test Printing (HR Printer)", "HR Printer"],
      ["Test Printing (Finance Printer)", "Finance Printer"],
      ["Accpac Server", "172.16.5.210"],
      ["Main Reception printer & Tab", "Guest Wifi"],
      ["Security Reception printer & Tab", "Guest Wifi"],
    ]),
  },
  {
    key: "kolkata",
    label: "Kolkata Daily Checklist",
    defaultFields: ["IP / Address"],
    defaultItems: [], // add items from the Manage tab
  },
  {
    key: "other",
    label: "Other Checklist",
    defaultFields: ["IP / Address"],
    defaultItems: makeItems([
      ["CNQ3KD5D33", "172.16.16.6"],
      ["CNQ3LBN306", "172.16.16.7"],
      ["CNQ3LBN307", "172.16.16.8"],
      ["CNQ3KD5D3R", "172.16.16.9"],
      ["CNQ3LBN2ZF", "172.16.16.10"],
      ["CNQ3LBN2YS", "172.16.16.11"],
      ["CNQ3LBN30F", "172.16.16.12"],
      ["CNQ3LBN30M", "172.16.16.13"],
      ["CNQ3LBN308", "172.16.16.14"],
      ["CNQ3LBN30D", "172.16.16.15"],
      ["CNQ3LBN2ZN", "172.16.16.16"],
      ["CNQ3LBN2ZH", "172.16.16.17"],
    ]),
  },
];

// ── Persistence layer (backend-backed, scoped per checklist key) ────────
//   GET  /api/checklist/:key/config           -> { fields, items }
//   PUT  /api/checklist/:key/fields            <- { fields }
//   PUT  /api/checklist/:key/items             <- { items }
//   GET  /api/checklist/:key/status/:date      -> { status }
//   PUT  /api/checklist/:key/status/:date      <- { status }
//   POST /api/checklist/:key/status/batch      <- { dates }  -> { [date]: {...} }
//   GET  /api/checklist/:key/history?limit=60  -> { dates, data }
//
// IMPORTANT: loadConfig distinguishes "the request failed" from "the
// checklist has genuinely never been configured". These must never be
// collapsed into the same value — doing so previously caused a real bug:
// any transient network/API failure (server restarting, wrong port,
// CORS, timeout) looked identical to "brand new checklist", which made
// the app silently push hardcoded defaults over real saved data. `ok:
// false` means "we don't know the state, don't touch anything".
async function loadConfig(key) {
  try {
    const res = await apiFetch(`/checklist/${key}/config`);
    return {
      fields: Array.isArray(res.fields) ? res.fields : [],
      items: Array.isArray(res.items) ? res.items : [],
      ok: true,
    };
  } catch (e) {
    console.error("loadConfig failed", key, e);
    return { fields: null, items: null, ok: false };
  }
}
async function saveFields(key, fields) {
  try { await apiFetch(`/checklist/${key}/fields`, { method: "PUT", body: JSON.stringify({ fields }) }); }
  catch (e) { console.error("saveFields failed", e); }
}
async function saveItems(key, items) {
  try { await apiFetch(`/checklist/${key}/items`, { method: "PUT", body: JSON.stringify({ items }) }); }
  catch (e) { console.error("saveItems failed", e); }
}
async function loadStatus(key, date) {
  try { const res = await apiFetch(`/checklist/${key}/status/${date}`); return res.status || {}; }
  catch (e) { return {}; }
}
async function saveStatus(key, date, statusMap) {
  try {
    await apiFetch(`/checklist/${key}/status/${date}`, { method: "PUT", body: JSON.stringify({ status: statusMap }) });
    return true;
  } catch (e) { console.error("saveStatus failed", e); return false; }
}
async function loadStatusBatch(key, dates) {
  try { return await apiFetch(`/checklist/${key}/status/batch`, { method: "POST", body: JSON.stringify({ dates }) }); }
  catch (e) { console.error("loadStatusBatch failed", e); return {}; }
}
async function loadHistoryFromApi(key, limit = 60) {
  try {
    const res = await apiFetch(`/checklist/${key}/history?limit=${limit}`);
    return { dates: res.dates || [], data: res.data || {} };
  } catch (e) { console.error("loadHistory failed", e); return { dates: [], data: {} }; }
}

// ── Ping target helpers ──────────────────────────────────────────────────
const IPV4_PATTERN = /^(\d{1,3}\.){3}\d{1,3}$/;
const HOSTNAME_PATTERN = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
function isPingableValue(v) {
  if (!v || typeof v !== "string") return false;
  const t = v.trim();
  if (!t) return false;
  return IPV4_PATTERN.test(t) || HOSTNAME_PATTERN.test(t);
}

export default function ITChecklist() {
  const [checklistKey, setChecklistKey] = useState("main");
  const activeChecklist = CHECKLISTS.find((c) => c.key === checklistKey);

  const [loading, setLoading] = useState(true);
  const [configError, setConfigError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  const [fields, setFields] = useState([]);
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(todayStr());
  const [statusMap, setStatusMap] = useState({});
  const [tab, setTab] = useState("checklist");
  const [search, setSearch] = useState("");
  const [sparklines, setSparklines] = useState({});
  const [noteEditor, setNoteEditor] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [newFieldName, setNewFieldName] = useState("");
  const [addingItem, setAddingItem] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", values: {} });
  const [editingItemId, setEditingItemId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [historyDates, setHistoryDates] = useState([]);
  const [historyData, setHistoryData] = useState({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [copyState, setCopyState] = useState("idle");
  const [saveState, setSaveState] = useState("idle");

  const [pingLoading, setPingLoading] = useState({});
  const [pingResults, setPingResults] = useState({});
  const [activePingId, setActivePingId] = useState(null);
  const [pingAllRunning, setPingAllRunning] = useState(false);
  const [pingAllProgress, setPingAllProgress] = useState({ done: 0, total: 0 });

  const [reportTo, setReportTo] = useState("");
  const [sendState, setSendState] = useState("idle");
  const [sendError, setSendError] = useState("");

  // ---------- load config whenever the active checklist changes ----------
  // Fix: previously, ANY failed request (network blip, backend restarting,
  // CORS, wrong port) was treated identically to "this checklist has never
  // been configured", which caused the app to silently overwrite real
  // items/fields with hardcoded defaults. Now a failed request shows an
  // error and touches nothing; defaults are only ever seeded when the
  // server explicitly confirms the checklist is empty.
  useEffect(() => {
    (async () => {
      setLoading(true);
      setConfigError(null);
      const { fields: loadedFields, items: loadedItems, ok } = await loadConfig(checklistKey);

      if (!ok) {
        setConfigError(
          "Couldn't reach the server to load this checklist. Your saved data is safe — check your connection and try again."
        );
        setLoading(false);
        return;
      }

      if (loadedFields.length === 0 && loadedItems.length === 0) {
        // Server explicitly confirmed there's nothing saved yet for this
        // checklist key — safe to seed defaults.
        await saveFields(checklistKey, activeChecklist.defaultFields);
        await saveItems(checklistKey, activeChecklist.defaultItems);
        setFields(activeChecklist.defaultFields);
        setItems(activeChecklist.defaultItems);
      } else {
        setFields(loadedFields.length ? loadedFields : activeChecklist.defaultFields);
        setItems(loadedItems.length ? loadedItems : activeChecklist.defaultItems);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklistKey, retryTick]);

  // ---------- status for selected date ----------
  const loadStatusForDate = useCallback(async (key, d) => {
    setStatusMap(await loadStatus(key, d));
  }, []);
  useEffect(() => { loadStatusForDate(checklistKey, date); }, [checklistKey, date, loadStatusForDate]);

  useEffect(() => { setPingResults({}); setPingLoading({}); setActivePingId(null); }, [date, checklistKey]);

  // ---------- last 14 days for sparklines ----------
  useEffect(() => {
    (async () => {
      const dates = [];
      const base = new Date(date + "T00:00:00");
      for (let i = 13; i >= 0; i--) {
        const d = new Date(base); d.setDate(d.getDate() - i);
        dates.push(d.toISOString().slice(0, 10));
      }
      setSparklines(await loadStatusBatch(checklistKey, dates));
    })();
  }, [date, checklistKey, items.length]);

  const persistStatus = async (map) => {
    setSaveState("saving");
    const ok = await saveStatus(checklistKey, date, map);
    setSaveState(ok ? "saved" : "error");
    setTimeout(() => setSaveState("idle"), 1200);
  };

  const cycleStatus = (itemId) => {
    const current = statusMap[itemId]?.status ?? null;
    const idx = STATUS_ORDER.indexOf(current);
    const next = STATUS_ORDER[(idx + 1) % STATUS_ORDER.length];
    const nextMap = { ...statusMap, [itemId]: { ...(statusMap[itemId] || {}), status: next } };
    if (next === null) delete nextMap[itemId];
    setStatusMap(nextMap);
    persistStatus(nextMap);
  };

  const setStatusDirect = (itemId, status, extraStatusMap) => {
    const base = extraStatusMap || statusMap;
    const nextMap = { ...base, [itemId]: { ...(base[itemId] || {}), status } };
    setStatusMap(nextMap);
    return nextMap;
  };

  const saveNote = (itemId) => {
    const nextMap = { ...statusMap, [itemId]: { ...(statusMap[itemId] || {}), note: noteDraft } };
    setStatusMap(nextMap);
    persistStatus(nextMap);
    setNoteEditor(null);
    setNoteDraft("");
  };

  // ---------- items / fields management ----------
  const persistItems = async (next) => { setItems(next); await saveItems(checklistKey, next); };
  const persistFields = async (next) => { setFields(next); await saveFields(checklistKey, next); };

  const addField = async () => {
    const name = newFieldName.trim();
    if (!name || fields.includes(name)) return;
    await persistFields([...fields, name]);
    setNewFieldName("");
  };
  const removeField = async (name) => {
    await persistFields(fields.filter((f) => f !== name));
    await persistItems(items.map((it) => { const v = { ...it.values }; delete v[name]; return { ...it, values: v }; }));
  };
  const startAddItem = () => {
    const blank = {}; fields.forEach((f) => (blank[f] = ""));
    setNewItem({ name: "", values: blank }); setAddingItem(true);
  };
  const confirmAddItem = async () => {
    if (!newItem.name.trim()) return;
    await persistItems([...items, { id: uid("item"), name: newItem.name.trim(), values: newItem.values }]);
    setAddingItem(false);
  };
  const startEdit = (item) => {
    setEditingItemId(item.id);
    const values = { ...item.values };
    fields.forEach((f) => { if (!(f in values)) values[f] = ""; });
    setEditDraft({ name: item.name, values });
  };
  const confirmEdit = async () => {
    await persistItems(items.map((it) => it.id === editingItemId ? { ...it, name: editDraft.name.trim(), values: editDraft.values } : it));
    setEditingItemId(null); setEditDraft(null);
  };
  const deleteItem = async (id) => { await persistItems(items.filter((it) => it.id !== id)); };

  // ---------- ping ----------
  const getPingTarget = useCallback((item) => {
    const ipField = fields.find((f) => /ip/i.test(f)) || fields[0];
    return ipField ? (item.values?.[ipField] || "").trim() : "";
  }, [fields]);

  // Pings one item. Returns the resulting status map so callers (pingAll)
  // can chain updates without racing React state batching.
  const pingItem = async (item, baseStatusMap) => {
    const host = getPingTarget(item);
    if (!isPingableValue(host)) return baseStatusMap ?? statusMap;

    setPingLoading((p) => ({ ...p, [item.id]: true }));
    let resultMap = baseStatusMap ?? statusMap;
    try {
      const res = await apiFetch("/checklist/ping", { method: "POST", body: JSON.stringify({ host }) });
      setPingResults((p) => ({ ...p, [item.id]: { reachable: !!res.reachable, checkedAt: res.checkedAt || new Date().toISOString() } }));
      if (res.reachable) {
        resultMap = setStatusDirect(item.id, "W", resultMap);
        await persistStatus(resultMap);
      }
    } catch (e) {
      setPingResults((p) => ({ ...p, [item.id]: { reachable: false, error: e.message, checkedAt: new Date().toISOString() } }));
    } finally {
      setPingLoading((p) => ({ ...p, [item.id]: false }));
    }
    return resultMap;
  };

  // "Ping all" — walks every pingable item ONE AT A TIME so the UI can
  // visibly step through them (pulsing highlight on whichever row is
  // currently being pinged) rather than firing everything at once.
  const pingAll = async () => {
    const targets = items.filter((it) => isPingableValue(getPingTarget(it)));
    if (!targets.length || pingAllRunning) return;
    setPingAllRunning(true);
    setPingAllProgress({ done: 0, total: targets.length });
    let runningMap = statusMap;
    for (let i = 0; i < targets.length; i++) {
      const it = targets[i];
      setActivePingId(it.id);
      runningMap = await pingItem(it, runningMap);
      setPingAllProgress({ done: i + 1, total: targets.length });
      await sleep(280); // beat between rows so the step-by-step animation reads clearly
    }
    setActivePingId(null);
    setPingAllRunning(false);
  };

  // ---------- history ----------
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    const { dates, data } = await loadHistoryFromApi(checklistKey, 60);
    setHistoryDates(dates); setHistoryData(data);
    setHistoryLoading(false);
  }, [checklistKey]);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab, checklistKey, loadHistory]);

  // ---------- derived ----------
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.name.toLowerCase().includes(q) || Object.values(it.values || {}).some((v) => String(v).toLowerCase().includes(q)));
  }, [items, search]);

  const summary = useMemo(() => {
    let w = 0, nw = 0, na = 0, pending = 0;
    items.forEach((it) => {
      const s = statusMap[it.id]?.status;
      if (s === "W") w++; else if (s === "NW") nw++; else if (s === "NA") na++; else pending++;
    });
    return { w, nw, na, pending, total: items.length };
  }, [items, statusMap]);

  const pingableCount = useMemo(() => items.filter((it) => isPingableValue(getPingTarget(it))).length, [items, getPingTarget]);

  // ---------- excel export ----------
  const exportExcel = async () => {
    let dates = historyDates, data = historyData;
    if (!dates.length) {
      const res = await loadHistoryFromApi(checklistKey, 60);
      dates = res.dates; data = res.data;
      setHistoryDates(dates); setHistoryData(data);
    }
    const orderedDates = dates.length ? dates.slice().reverse() : [date];
    const header1 = ["DATE", ...items.map((it) => it.name)];
    const fieldRows = fields.map((f) => ["", ...items.map((it) => it.values?.[f] ?? "")]);
    const rows = orderedDates.map((d) => {
      const dayMap = d === date ? statusMap : (data[d] || {});
      return [d, ...items.map((it) => dayMap[it.id]?.status ?? "")];
    });
    const aoa = [[fields.join(" / "), ...items.map(() => "")], ...fieldRows, header1, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 14 }, ...items.map(() => ({ wch: 16 }))];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, activeChecklist.label.slice(0, 28));
    XLSX.writeFile(wb, `IT_Checklist_${checklistKey}_${date}.xlsx`);
  };

  // ---------- email report ----------
  // Table-based, no external/CID images — the previous version referenced
  // `cid:company_logo` for a logo that was never actually attached, which
  // renders as a broken-image glyph in Outlook. Removed entirely.
  const buildReportHtml = () => {
    const rows = items.map((it) => {
      const s = statusMap[it.id]?.status;
      const cfg = s ? STATUS[s] : { label: "Not checked", color: "#6B7280", bg: "#F3F4F6", border: "#E5E7EB" };
      const note = statusMap[it.id]?.note;
      const fieldStr = fields.map((f) => it.values?.[f]).filter(Boolean).join(" · ");
      return `<tr>
        <td style="padding:10px 12px;border-bottom:1px solid #EEF0F3;font-size:13px;color:#111827;font-weight:600;font-family:Arial,Helvetica,sans-serif;">${escapeHtml(it.name)}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #EEF0F3;font-size:12px;color:#6B7280;font-family:'Courier New',monospace;">${escapeHtml(fieldStr)}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #EEF0F3;font-family:Arial,Helvetica,sans-serif;">
          <table cellpadding="0" cellspacing="0" border="0"><tr>
            <td bgcolor="${cfg.bg}" style="background:${cfg.bg};border:1px solid ${cfg.border};padding:3px 10px;font-size:12px;font-weight:700;color:${cfg.color};font-family:Arial,Helvetica,sans-serif;">${escapeHtml(cfg.label)}</td>
          </tr></table>
          ${note ? `<div style="font-size:12px;color:#6B7280;margin-top:4px;font-family:Arial,Helvetica,sans-serif;">${escapeHtml(note)}</div>` : ""}
        </td>
      </tr>`;
    }).join("");

    return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" lang="en">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <!--[if mso]><xml><o:OfficeDocumentSettings><o:AllowPNG/><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml><![endif]-->
  <title>Daily System Checklist</title>
</head>
<body style="margin:0;padding:0;background:#f0f4f8;font-family:Arial,Helvetica,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f0f4f8;padding:28px 0;">
  <tr><td align="center" valign="top">
    <table width="640" cellpadding="0" cellspacing="0" border="0" style="width:640px;max-width:100%;background:#ffffff;border:1px solid #E5E7EB;">
      <tr>
        <td bgcolor="#111827" style="background:#111827;padding:24px 28px;">
          <div style="color:#9CA3AF;font-size:11px;letter-spacing:1px;text-transform:uppercase;font-family:Arial,Helvetica,sans-serif;">Mindteck IT Operations &middot; ${escapeHtml(activeChecklist.label)}</div>
          <div style="color:#ffffff;font-size:20px;font-weight:700;margin-top:4px;font-family:Arial,Helvetica,sans-serif;">Daily System Checklist</div>
          <div style="color:#D1D5DB;font-size:13px;margin-top:2px;font-family:Arial,Helvetica,sans-serif;">${formatDateLong(date)}</div>
        </td>
      </tr>
      <tr>
        <td bgcolor="#F9FAFB" style="background:#F9FAFB;padding:16px 20px;border-left:1px solid #E5E7EB;border-right:1px solid #E5E7EB;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
            ${statPill(summary.w, "Working", "#15803D", "#DCFCE7")}<td width="8"></td>
            ${statPill(summary.nw, "Not working", "#B91C1C", "#FEE2E2")}<td width="8"></td>
            ${statPill(summary.na, "N/A", "#B45309", "#FEF3C7")}<td width="8"></td>
            ${statPill(summary.pending, "Not checked", "#374151", "#F3F4F6")}
          </tr></table>
        </td>
      </tr>
      <tr>
        <td style="border-left:1px solid #E5E7EB;border-right:1px solid #E5E7EB;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
            <thead><tr bgcolor="#F9FAFB">
              <th align="left" style="padding:8px 12px;font-size:11px;color:#6B7280;text-transform:uppercase;letter-spacing:0.5px;border-bottom:1px solid #E5E7EB;font-family:Arial,Helvetica,sans-serif;">Item</th>
              <th align="left" style="padding:8px 12px;font-size:11px;color:#6B7280;text-transform:uppercase;letter-spacing:0.5px;border-bottom:1px solid #E5E7EB;font-family:Arial,Helvetica,sans-serif;">Reference</th>
              <th align="left" style="padding:8px 12px;font-size:11px;color:#6B7280;text-transform:uppercase;letter-spacing:0.5px;border-bottom:1px solid #E5E7EB;font-family:Arial,Helvetica,sans-serif;">Status</th>
            </tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </td>
      </tr>
      <tr>
        <td bgcolor="#ffffff" style="background:#ffffff;padding:16px 28px;border:1px solid #E5E7EB;border-top:none;">
          <div style="font-size:11px;color:#9CA3AF;font-family:Arial,Helvetica,sans-serif;">Generated automatically from the IT Daily Checklist tracker.</div>
        </td>
      </tr>
    </table>
  </td></tr>
</table>
</body>
</html>`;
  };

  const buildReportText = () => {
    const lines = [];
    lines.push(`IT DAILY CHECKLIST - ${activeChecklist.label} - ${formatDateLong(date)}`);
    lines.push("");
    lines.push(`Working: ${summary.w}   Not working: ${summary.nw}   N/A: ${summary.na}   Not checked: ${summary.pending}`);
    lines.push("");
    items.forEach((it) => {
      const s = statusMap[it.id]?.status;
      const cfg = s ? STATUS[s] : null;
      const note = statusMap[it.id]?.note ? `  (${statusMap[it.id].note})` : "";
      lines.push(`- ${it.name}: ${cfg ? cfg.label : "Not checked"}${note}`);
    });
    lines.push(""); lines.push("Sent from the IT Daily Checklist tracker.");
    return lines.join("\n");
  };

  const copyReport = async () => {
    const html = buildReportHtml(); const text = buildReportText();
    try {
      if (window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        })]);
      } else { await navigator.clipboard.writeText(text); }
      setCopyState("copied"); setTimeout(() => setCopyState("idle"), 1800);
    } catch (e) {
      try { await navigator.clipboard.writeText(text); setCopyState("copied"); }
      catch (e2) { setCopyState("failed"); }
      setTimeout(() => setCopyState("idle"), 1800);
    }
  };
  const openMail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(`IT Daily Checklist Report - ${activeChecklist.label} - ${date}`)}&body=${encodeURIComponent(buildReportText())}`;
  };
  const sendReportEmail = async () => {
    const recipients = reportTo.split(",").map((e) => e.trim()).filter(Boolean);
    if (!recipients.length) { setSendState("error"); setSendError("Enter at least one recipient email"); return; }
    setSendState("sending"); setSendError("");
    try {
      await apiFetch("/checklist/send-report", {
        method: "POST",
        body: JSON.stringify({ to: recipients.join(", "), subject: `IT Daily Checklist Report - ${activeChecklist.label} - ${date}`, html: buildReportHtml() }),
      });
      setSendState("sent"); setTimeout(() => setSendState("idle"), 2500);
    } catch (e) { setSendState("error"); setSendError(e.message || "Failed to send"); }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 0", color: "#6B7280", gap: 8 }}>
        <Loader2 className="animate-spin" size={18} /> Loading checklist…
      </div>
    );
  }

  if (configError) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 24px", color: "#F87171", gap: 10, textAlign: "center", background: "#0B1220", borderRadius: 14, border: "1px solid #1E293B", minHeight: 300 }}>
        <AlertTriangle size={24} />
        <div style={{ fontSize: 14, maxWidth: 420 }}>{configError}</div>
        <button
          className="itc-btn primary"
          style={{ background: "#2563EB", border: "1px solid #2563EB", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", marginTop: 4 }}
          onClick={() => setRetryTick((t) => t + 1)}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif", background: "#0B1220", minHeight: 600, borderRadius: 14, padding: 0, color: "#E7ECF4", overflow: "hidden", border: "1px solid #1E293B" }}>
      <style>{`
        .sg { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif; font-weight: 700; }
        .mono { font-family: 'Consolas', 'Courier New', monospace; }
        .itc-btn { background:#141C2E; border:1px solid #263248; color:#CBD5E1; padding:8px 12px; border-radius:8px; font-size:13px; display:inline-flex; align-items:center; gap:6px; cursor:pointer; transition:all .15s; }
        .itc-btn:hover { background:#1C2740; border-color:#334158; }
        .itc-btn:disabled { opacity:0.45; cursor:not-allowed; }
        .itc-btn.primary { background:#2563EB; border-color:#2563EB; color:#fff; }
        .itc-btn.primary:hover { background:#1D4ED8; }
        .itc-btn.accent { background:#7C3AED; border-color:#7C3AED; color:#fff; }
        .itc-btn.accent:hover { background:#6D28D9; }
        .itc-input { background:#0F172A; border:1px solid #263248; color:#E7ECF4; padding:7px 10px; border-radius:7px; font-size:13px; outline:none; }
        .itc-input:focus { border-color:#3B82F6; }
        .itc-tab { padding:9px 14px; border-radius:8px; font-size:13px; cursor:pointer; display:inline-flex; align-items:center; gap:6px; color:#8B95A8; }
        .itc-tab.active { background:#141C2E; color:#E7ECF4; }
        .itc-row:hover { background:#0F1729; }
        .cl-pill { padding:7px 13px; border-radius:999px; font-size:12px; cursor:pointer; border:1px solid #263248; color:#8B95A8; background:#0F1729; }
        .cl-pill.active { background:#1D4ED8; border-color:#1D4ED8; color:#fff; }
        @keyframes itcPulse { 0% { box-shadow: 0 0 0 0 rgba(124,58,237,0.55); } 70% { box-shadow: 0 0 0 9px rgba(124,58,237,0); } 100% { box-shadow: 0 0 0 0 rgba(124,58,237,0); } }
        .itc-pinging-row { animation: itcPulse 1.1s ease-out infinite; background:#161231 !important; }
      `}</style>

      {/* Checklist switcher */}
      <div style={{ padding: "16px 24px 0", display: "flex", gap: 8, flexWrap: "wrap" }}>
        {CHECKLISTS.map((c) => (
          <div key={c.key} className={`cl-pill ${checklistKey === c.key ? "active" : ""}`} onClick={() => setChecklistKey(c.key)}>
            {c.label}
          </div>
        ))}
      </div>

      {/* Header */}
      <div style={{ padding: "16px 24px 20px", borderBottom: "1px solid #1E293B", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: "#1D4ED8", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Server size={18} color="#fff" />
          </div>
          <div>
            <div className="sg" style={{ fontSize: 17, color: "#F1F5F9" }}>{activeChecklist.label}</div>
            <div style={{ fontSize: 12, color: "#64748B" }}>{items.length} systems tracked &middot; Mindteck IT Daily Checklist</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button className="itc-btn accent" onClick={pingAll} disabled={pingAllRunning || !pingableCount}>
            {pingAllRunning ? <Loader2 size={14} className="animate-spin" /> : <Radar size={14} />}
            {pingAllRunning ? `Pinging ${pingAllProgress.done}/${pingAllProgress.total}…` : `Ping all (${pingableCount})`}
          </button>
          <button className="itc-btn" onClick={exportExcel}><Download size={14} /> Export Excel</button>
          <button className="itc-btn primary" onClick={() => setEmailOpen(true)}><Mail size={14} /> Email report</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ padding: "12px 24px 0", display: "flex", gap: 6, borderBottom: "1px solid #1E293B" }}>
        <div className={`itc-tab ${tab === "checklist" ? "active" : ""}`} onClick={() => setTab("checklist")}><ListChecks size={14} /> Today's checklist</div>
        <div className={`itc-tab ${tab === "manage" ? "active" : ""}`} onClick={() => setTab("manage")}><Settings size={14} /> Manage items & fields</div>
        <div className={`itc-tab ${tab === "history" ? "active" : ""}`} onClick={() => setTab("history")}><History size={14} /> History</div>
      </div>

      {tab === "checklist" && (
        <div style={{ padding: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button className="itc-btn" onClick={() => setDate(shiftDate(date, -1))}><ChevronLeft size={14} /></button>
              <input type="date" className="itc-input" value={date} onChange={(e) => setDate(e.target.value)} />
              <button className="itc-btn" onClick={() => setDate(shiftDate(date, 1))}><ChevronRight size={14} /></button>
              <button className="itc-btn" onClick={() => setDate(todayStr())}>Today</button>
              {saveState === "saving" && <span style={{ fontSize: 12, color: "#64748B", display: "flex", alignItems: "center", gap: 4 }}><Loader2 size={12} className="animate-spin" /> Saving…</span>}
              {saveState === "saved" && <span style={{ fontSize: 12, color: "#4ADE80", display: "flex", alignItems: "center", gap: 4 }}><Check size={12} /> Saved</span>}
              {saveState === "error" && <span style={{ fontSize: 12, color: "#F87171", display: "flex", alignItems: "center", gap: 4 }}><AlertTriangle size={12} /> Save failed — check API</span>}
            </div>
            <div style={{ position: "relative" }}>
              <Search size={14} style={{ position: "absolute", left: 10, top: 10, color: "#64748B" }} />
              <input className="itc-input" style={{ paddingLeft: 30, width: 200 }} placeholder="Search items…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
            {chip("Working", summary.w, STATUS.W)}
            {chip("Not working", summary.nw, STATUS.NW)}
            {chip("N/A", summary.na, STATUS.NA)}
            {chip("Not checked", summary.pending, { color: "#94A3B8", bg: "#1E293B", border: "#334155" })}
          </div>

          <div style={{ border: "1px solid #1E293B", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: `1fr ${fields.map(() => "160px").join(" ")} 120px 100px 150px 130px`, background: "#0F1729", padding: "10px 14px", fontSize: 11, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5 }}>
              <div>System</div>
              {fields.map((f) => <div key={f}>{f}</div>)}
              <div>Ping</div>
              <div>14-day trend</div>
              <div>Status</div>
              <div>Remark</div>
            </div>
            {filteredItems.map((it) => {
              const s = statusMap[it.id]?.status;
              const cfg = s ? STATUS[s] : null;
              const pingTarget = getPingTarget(it);
              const pingable = isPingableValue(pingTarget);
              const isPinging = !!pingLoading[it.id];
              const pingResult = pingResults[it.id];
              const isActivePing = activePingId === it.id;
              return (
                <div
                  key={it.id}
                  className={`itc-row ${isActivePing ? "itc-pinging-row" : ""}`}
                  style={{ display: "grid", gridTemplateColumns: `1fr ${fields.map(() => "160px").join(" ")} 120px 100px 150px 130px`, padding: "10px 14px", borderTop: "1px solid #16202F", alignItems: "center", fontSize: 13 }}
                >
                  <div style={{ fontWeight: 500, color: "#E7ECF4" }}>{it.name}</div>
                  {fields.map((f) => <div key={f} className="mono" style={{ fontSize: 12, color: "#8FA3C4" }}>{it.values?.[f] || "—"}</div>)}
                  <div>
                    {pingable ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <button className="itc-btn" style={{ padding: "4px 8px", fontSize: 11, width: "fit-content" }} disabled={isPinging} onClick={() => pingItem(it)} title={`Ping ${pingTarget}`}>
                          {isPinging ? <Loader2 size={12} className="animate-spin" /> : <Activity size={12} />}
                          {isPinging ? "Pinging…" : "Ping"}
                        </button>
                        {pingResult && !isPinging && (
                          <span style={{ fontSize: 10, color: pingResult.reachable ? "#4ADE80" : "#F87171", display: "flex", alignItems: "center", gap: 3 }}>
                            {pingResult.reachable ? <Check size={10} /> : <X size={10} />}
                            {pingResult.reachable ? "Reachable" : "No response"}
                          </span>
                        )}
                      </div>
                    ) : <span style={{ fontSize: 11, color: "#334155" }}>—</span>}
                  </div>
                  <div style={{ display: "flex", gap: 2 }}>
                    {Object.keys(sparklines).sort().map((d) => {
                      const st = sparklines[d]?.[it.id]?.status;
                      const c = st ? STATUS[st].color : "#243044";
                      return <div key={d} title={d + (st ? ": " + STATUS[st].label : "")} style={{ width: 6, height: 16, borderRadius: 2, background: c }} />;
                    })}
                  </div>
                  <button onClick={() => cycleStatus(it.id)} style={{ padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", background: cfg ? cfg.bg : "#1E293B", color: cfg ? cfg.color : "#94A3B8", border: `1px solid ${cfg ? cfg.border : "#334155"}`, width: "fit-content" }}>
                    {cfg ? cfg.label : "Not checked"}
                  </button>
                  <div>
                    {noteEditor === it.id ? (
                      <div style={{ display: "flex", gap: 4 }}>
                        <input autoFocus className="itc-input" style={{ width: 90, fontSize: 12 }} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveNote(it.id)} />
                        <button className="itc-btn" style={{ padding: "4px 6px" }} onClick={() => saveNote(it.id)}><Check size={12} /></button>
                      </div>
                    ) : (
                      <button className="itc-btn" style={{ padding: "4px 8px", fontSize: 11, color: statusMap[it.id]?.note ? "#CBD5E1" : "#475569" }} onClick={() => { setNoteEditor(it.id); setNoteDraft(statusMap[it.id]?.note || ""); }}>
                        {statusMap[it.id]?.note ? truncate(statusMap[it.id].note, 14) : "Add note"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredItems.length === 0 && <div style={{ padding: 24, textAlign: "center", color: "#64748B", fontSize: 13 }}>No items match your search.</div>}
          </div>
        </div>
      )}

      {tab === "manage" && (
        <div style={{ padding: 24 }}>
          <div style={{ marginBottom: 24 }}>
            <div className="sg" style={{ fontSize: 14, marginBottom: 10, color: "#F1F5F9" }}>Custom fields — {activeChecklist.label}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
              {fields.map((f) => (
                <span key={f} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#141C2E", border: "1px solid #263248", padding: "6px 10px", borderRadius: 999, fontSize: 12, color: "#CBD5E1" }}>
                  {f}<X size={12} style={{ cursor: "pointer", color: "#94A3B8" }} onClick={() => removeField(f)} />
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="itc-input" placeholder="e.g. Location, Vendor, Warranty" value={newFieldName} onChange={(e) => setNewFieldName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addField()} style={{ width: 240 }} />
              <button className="itc-btn primary" onClick={addField}><Plus size={14} /> Add field</button>
            </div>
          </div>

          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div className="sg" style={{ fontSize: 14, color: "#F1F5F9" }}>Checklist items ({items.length})</div>
              {!addingItem && <button className="itc-btn primary" onClick={startAddItem}><Plus size={14} /> Add item</button>}
            </div>

            {addingItem && (
              <div style={{ border: "1px solid #263248", borderRadius: 10, padding: 14, marginBottom: 12, background: "#0F1729" }}>
                <input className="itc-input" placeholder="Item name" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} style={{ width: "100%", marginBottom: 8 }} />
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                  {fields.map((f) => (
                    <input key={f} className="itc-input" placeholder={f} value={newItem.values[f] || ""} onChange={(e) => setNewItem({ ...newItem, values: { ...newItem.values, [f]: e.target.value } })} style={{ width: 180 }} />
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="itc-btn primary" onClick={confirmAddItem}><Save size={14} /> Save item</button>
                  <button className="itc-btn" onClick={() => setAddingItem(false)}>Cancel</button>
                </div>
              </div>
            )}

            <div style={{ border: "1px solid #1E293B", borderRadius: 10, overflow: "hidden" }}>
              {items.map((it) => (
                <div key={it.id} className="itc-row" style={{ padding: 12, borderTop: "1px solid #16202F" }}>
                  {editingItemId === it.id ? (
                    <div>
                      <input className="itc-input" value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} style={{ width: "100%", marginBottom: 8 }} />
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                        {fields.map((f) => (
                          <input key={f} className="itc-input" placeholder={f} value={editDraft.values[f] || ""} onChange={(e) => setEditDraft({ ...editDraft, values: { ...editDraft.values, [f]: e.target.value } })} style={{ width: 180 }} />
                        ))}
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="itc-btn primary" onClick={confirmEdit}><Save size={14} /> Save</button>
                        <button className="itc-btn" onClick={() => setEditingItemId(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "#E7ECF4" }}>{it.name}</div>
                        <div className="mono" style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{fields.map((f) => it.values?.[f]).filter(Boolean).join("  ·  ") || "—"}</div>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="itc-btn" style={{ padding: 7 }} onClick={() => startEdit(it)}><Pencil size={13} /></button>
                        <button className="itc-btn" style={{ padding: 7, color: "#F87171" }} onClick={() => deleteItem(it.id)}><Trash2 size={13} /></button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {items.length === 0 && <div style={{ padding: 24, textAlign: "center", color: "#64748B", fontSize: 13 }}>No items yet — add the first one above.</div>}
            </div>
          </div>
        </div>
      )}

      {tab === "history" && (
        <div style={{ padding: 24 }}>
          {historyLoading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#64748B", padding: 20 }}><Loader2 size={16} className="animate-spin" /> Loading history…</div>
          ) : historyDates.length === 0 ? (
            <div style={{ color: "#64748B", fontSize: 13, padding: 20 }}>No history recorded yet. Mark statuses on the checklist tab to build history.</div>
          ) : (
            <div style={{ overflowX: "auto", border: "1px solid #1E293B", borderRadius: 10 }}>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#0F1729" }}>
                    <th style={{ position: "sticky", left: 0, background: "#0F1729", padding: "8px 12px", textAlign: "left", color: "#64748B", fontWeight: 500 }}>Date</th>
                    {items.map((it) => <th key={it.id} style={{ padding: "8px 10px", color: "#94A3B8", fontWeight: 500, whiteSpace: "nowrap", textAlign: "center" }}>{it.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {historyDates.map((d) => (
                    <tr key={d} style={{ borderTop: "1px solid #16202F" }}>
                      <td className="mono" style={{ position: "sticky", left: 0, background: "#0B1220", padding: "6px 12px", color: "#CBD5E1", whiteSpace: "nowrap" }}>{d}</td>
                      {items.map((it) => {
                        const st = historyData[d]?.[it.id]?.status;
                        const cfg = st ? STATUS[st] : null;
                        return <td key={it.id} style={{ padding: "6px 10px", textAlign: "center" }}>{cfg ? <span style={{ color: cfg.color, fontWeight: 600 }}>{cfg.short}</span> : <span style={{ color: "#334155" }}>—</span>}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {emailOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 20 }} onClick={() => setEmailOpen(false)}>
          <div style={{ background: "#0F1729", borderRadius: 14, border: "1px solid #263248", maxWidth: 640, width: "100%", maxHeight: "85vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #1E293B", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div className="sg" style={{ fontSize: 15, color: "#F1F5F9" }}>Email report preview — {activeChecklist.label}</div>
              <X size={18} style={{ cursor: "pointer", color: "#94A3B8" }} onClick={() => setEmailOpen(false)} />
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #263248" }} dangerouslySetInnerHTML={{ __html: buildReportHtml() }} />
            </div>
            <div style={{ padding: "0 20px", marginBottom: 4 }}>
              <div style={{ border: "1px solid #263248", borderRadius: 10, padding: 14, background: "#0B1220" }}>
                <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}><Send size={13} /> Send this report by email now</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input className="itc-input" style={{ flex: 1, minWidth: 220 }} placeholder="recipient@mindteck.com, another@mindteck.com" value={reportTo} onChange={(e) => { setReportTo(e.target.value); if (sendState === "error") setSendState("idle"); }} />
                  <button className="itc-btn primary" onClick={sendReportEmail} disabled={sendState === "sending"}>
                    {sendState === "sending" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {sendState === "sending" ? "Sending…" : sendState === "sent" ? "Sent!" : "Send email"}
                  </button>
                </div>
                {sendState === "error" && <div style={{ fontSize: 12, color: "#F87171", marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}><AlertTriangle size={12} /> {sendError}</div>}
                {sendState === "sent" && <div style={{ fontSize: 12, color: "#4ADE80", marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}><Check size={12} /> Report emailed successfully.</div>}
              </div>
            </div>
            <div style={{ padding: "16px 20px 20px", display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="itc-btn" onClick={openMail}><Mail size={14} /> Open in mail app instead</button>
              <button className="itc-btn" onClick={copyReport}><Copy size={14} /> {copyState === "copied" ? "Copied!" : copyState === "failed" ? "Copy failed" : "Copy formatted report"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function chip(label, count, cfg) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#0F1729", border: `1px solid ${cfg.border || "#263248"}`, borderRadius: 10, padding: "8px 14px" }}>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.color }} />
      <span style={{ fontSize: 12, color: "#94A3B8" }}>{label}</span>
      <span className="sg" style={{ fontSize: 15, color: "#E7ECF4" }}>{count}</span>
    </div>
  );
}
function statPill(count, label, color, bg) {
  return `<td width="25%" align="center" bgcolor="${bg}" style="background:${bg};border-radius:6px;padding:10px 6px;">
    <div style="font-size:18px;font-weight:700;color:${color};font-family:Arial,Helvetica,sans-serif;">${count}</div>
    <div style="font-size:10px;color:${color};text-transform:uppercase;letter-spacing:0.5px;margin-top:2px;font-family:Arial,Helvetica,sans-serif;">${escapeHtml(label)}</div>
  </td>`;
}
function escapeHtml(s) { return String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])); }
function truncate(s, n) { return s.length > n ? s.slice(0, n) + "…" : s; }
function shiftDate(d, delta) { const dt = new Date(d + "T00:00:00"); dt.setDate(dt.getDate() + delta); return dt.toISOString().slice(0, 10); }
function formatDateLong(d) { const dt = new Date(d + "T00:00:00"); return dt.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" }); }
